import React, { useEffect } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DivisionOption {
  id: string;
  name: string;
}

type SingleMode = {
  mode?: never;
  value?: string;
  onChange?: (value: string) => void;
};

type MultipleMode = {
  mode: 'multiple';
  value?: string[];
  onChange?: (value: string[]) => void;
};

type DropDownDivisionsProps = {
  options?: DivisionOption[];
  disabled?: boolean;
  placeholder?: string;
} & Omit<SelectProps, 'onChange' | 'value' | 'mode' | 'options'> &
  (SingleMode | MultipleMode);

// ─── Component ───────────────────────────────────────────────────────────────

const DropDownDivisions = ({
  options = [],
  onChange,
  disabled = false,
  placeholder,
  mode,
  value,
  ...rest
}: DropDownDivisionsProps): JSX.Element => {
  // Auto-select when only one option is available and no value is set
  useEffect(() => {
    if (options.length !== 1 || value !== undefined) return;

    const singleId = options[0].id;

    if (mode === 'multiple') {
      (onChange as (v: string[]) => void)([singleId]);
    } else {
      (onChange as (v: string) => void)(singleId);
    }
  }, [options, value, mode, onChange]);

  return (
    <div className="gx-w-100">
      <Select
        {...rest}
        mode={mode}
        value={value}
        disabled={disabled}
        style={{ width: '100%' }}
        dropdownMatchSelectWidth={false}
        onChange={onChange as SelectProps['onChange']}
        optionFilterProp="children"
        placeholder={placeholder}
        showSearch
      >
        {options.map(div => (
          <Select.Option key={div.id} value={div.id}>
            {div.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default React.memo(DropDownDivisions);
